import { useEffect, useMemo } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { getEnv } from '../../Context/env';
import { FamilyName, MerchantId, PaymentMethodTypes } from '../constants.ts';

const useGooglePay = (googlePayButtonContainer, billingDetails, onSuccess, onError, onReady) => {
  const {
    isMethodChange,
    upgradeOffer,
    isTrial,
    price,
    proRataPrice,
    isProRataMovement,
    billingDateChangeProRataPrice,
  } = billingDetails;

  const recurly = useRecurly();
  const analyticsIdentifier =
    isMethodChange || billingDateChangeProRataPrice ? PaymentMethodTypes.GooglePay : 'new_gpay';

  const isGooglePayButtonReady = googlePayButtonContainer;

  const env = getEnv();

  const displayItems = useMemo(() => {
    const items = [];

    if (billingDateChangeProRataPrice) {
      items.push({
        label: 'Billing date change adjustment',
        type: 'SUBTOTAL',
        price: billingDateChangeProRataPrice,
      });
    } else {
      if (isProRataMovement) {
        items.push({
          label: "Today's payment",
          type: 'LINE_ITEM',
          price: proRataPrice,
        });
      }

      items.push({
        label: FamilyName[upgradeOffer?.family],
        type: 'SUBTOTAL',
        price: isTrial ? 0 : price,
      });
    }

    return items;
  }, [billingDateChangeProRataPrice, isProRataMovement, proRataPrice, upgradeOffer, isTrial, price]);

  const totalPrice = useMemo(
    () => displayItems.reduce((sum, item) => (parseFloat(sum) + parseFloat(item.price)).toFixed(2), '0.00'),
    [displayItems]
  );

  const googlePayOptions = useMemo(
    () => ({
      currency: 'GBP',
      country: 'GB',
      total: totalPrice,
      displayItems,
      gatewayCode: env.REACT_APP_GOOGLE_PAY_GATEWAY_CODE,
      googleMerchantId: MerchantId.GooglePay,
      billingAddressRequired: true,
      buttonOptions: {
        buttonType: 'pay',
        buttonSizeMode: 'fill',
        buttonColor: 'black',
      },
      callbacks: {
        onPaymentAuthorized: async paymentData => {
          const { recurlyToken } = paymentData;
          if (recurlyToken) {
            onSuccess(recurlyToken, analyticsIdentifier);
          } else {
            onError(new Error('Tokenization failed'), analyticsIdentifier);
          }
        },
      },
    }),
    [totalPrice, displayItems, analyticsIdentifier, onSuccess, onError]
  );

  useEffect(() => {
    if (!googlePayButtonContainer || !recurly) return undefined;

    let googlePay;

    try {
      googlePay = recurly.GooglePay(googlePayOptions);

      if (!googlePay) {
        onError(new Error('Google Pay initialization failed'), analyticsIdentifier);
        return undefined;
      }
    } catch (error) {
      onError(error, analyticsIdentifier);
      return undefined;
    }

    googlePay.on('ready', googlePayButtonElement => {
      if (googlePayButtonContainer && googlePayButtonContainer.appendChild) {
        googlePayButtonContainer.replaceChildren(googlePayButtonElement);
      }
      onReady?.(true);
    });

    googlePay.on('error', err => {
      onError(err, analyticsIdentifier);
    });

    return () => {
      if (googlePay) {
        googlePay.off();
      }
    };
  }, [isGooglePayButtonReady, googlePayButtonContainer, googlePayOptions, recurly, onError, analyticsIdentifier]);
};

export default useGooglePay;
